import { Controller } from '@hotwired/stimulus'
import { selectedLocationRow, selectedLocationPagination } from '../components/shelter_diagnostics'

const STORAGE_KEY = 'shelter_diagnostics_state'

export default class extends Controller {
  static targets = [
    'detailFrame',
    'actionBar',
    'selectedLocations',
    'selectedLocationsList',
    'filterToggle',
    'rebootComputer',
    'shelterOnlyControls',
    'roofLightOn',
    'roofLightOff',
    'rebootToplight',
    'rebootEpaper'
  ];

  static values = { selections: Array, showSelectedOnly: { type: Boolean, default: false }, selectionPage: { type: Number, default: 0 } };

  get selectedIds () {
    return this.selectionsValue.map(s => s.id)
  }

  get allUrbanPanel () {
    return this.selectionsValue.length > 0 &&
      this.selectionsValue.every(s => s.category === 'urban_panel')
  }

  connect () {
    this.boundMarkCheckboxes = this.markSelectedCheckboxes.bind(this)
    this.element.addEventListener('turbo:frame-load', this.boundMarkCheckboxes)
    this.restoreState()
  }

  disconnect () {
    this.element.removeEventListener('turbo:frame-load', this.boundMarkCheckboxes)
  }

  closeDetail (event) {
    event.preventDefault()
    this.selectedLocationId = null
    this.detailFrameTarget.innerHTML = ''
    this.detailFrameTarget.removeAttribute('src')
    this.saveState()
  }

  openLocation (event) {
    this.selectedLocationId = event.currentTarget.dataset.locationId
    this.showSelectedLocation()
    this.saveState()
  }

  showSelectedLocation () {
    this.detailFrameTarget.src = `/diagnostics/kit_detail?location_id=${this.selectedLocationId}`
  }

  selectLocation (event) {
    const el = event.currentTarget
    const id = parseInt(el.value)
    const checked = el.checked

    if (checked) {
      const category = el.dataset.locationCategory || ''
      const name = el.dataset.locationName || ''
      const state = el.dataset.locationState || ''
      this.selectionsValue = [...this.selectionsValue, { id, category, name, state }]
    } else {
      this.selectionsValue = this.selectionsValue.filter(s => s.id !== id)
    }

    if (this.selectionsValue.length === 0 && this.showSelectedOnlyValue) {
      this.showSelectedOnlyValue = false
    }

    this.renderSelectedLocations()
    this.applySelectedFilter()
    this.updateFilterToggle()
    this.updatePowerControls()
    this.saveState()
  }

  deselectLocation (event) {
    const id = parseInt(event.currentTarget.dataset.deselectId)
    this.selectionsValue = this.selectionsValue.filter(s => s.id !== id)
    const cb = this.element.querySelector(`input[data-location-id="${id}"]`)
    if (cb) cb.checked = false

    if (this.selectionsValue.length === 0 && this.showSelectedOnlyValue) {
      this.showSelectedOnlyValue = false
    }

    this.renderSelectedLocations()
    this.applySelectedFilter()
    this.updateFilterToggle()
    this.updatePowerControls()
    this.saveState()
  }

  clearSavedState () {
    localStorage.removeItem(STORAGE_KEY)
    this.clearSelectedLocations()
  }

  clearSelectedLocations () {
    this.element.querySelectorAll('input[data-location-id]').forEach(cb => {
      cb.checked = false
    })
    this.selectionsValue = []
    this.selectionPageValue = 0
    this.showSelectedOnlyValue = false
    this.selectedLocationId = null
    this.renderSelectedLocations()
    this.applySelectedFilter()
    this.updateFilterToggle()
    this.updatePowerControls()
    this.saveState()
  }

  filterSelectedOnly () {
    this.showSelectedOnlyValue = !this.showSelectedOnlyValue
    this.applySelectedFilter()
    this.updateFilterToggle()
    this.saveState()
  }

  applySelectedFilter () {
    const ids = this.selectedIds
    const rows = this.element.querySelectorAll('[data-action*="openLocation"]')
    rows.forEach(row => {
      if (!this.showSelectedOnlyValue) {
        row.classList.remove('hidden')
        return
      }
      const id = parseInt(row.dataset.locationId)
      row.classList.toggle('hidden', !ids.includes(id))
    })
  }

  updateFilterToggle () {
    this.filterToggleTargets.forEach(toggle => {
      toggle.hidden = this.selectionsValue.length === 0
      toggle.textContent = this.showSelectedOnlyValue
        ? 'Show All'
        : 'Show Selected Only'
    })
  }

  updatePowerControls () {
    if (!this.hasShelterOnlyControlsTarget) return
    this.shelterOnlyControlsTarget.hidden = this.allUrbanPanel
  }

  showSharedActions () {
  }

  hideSharedActions () {
  }

  showSelectedLocations () {
    this.selectedLocationsTarget.querySelector('span').hidden = true
    this.selectedLocationsTarget.querySelector('div').hidden = false
    this.selectedLocationsTarget.querySelector('button.dropdown-toggle span').innerHTML = `${this.selectionsValue.length} Locations Selected`
    this.showSharedActions()
  }

  hideSelectedLocations () {
    this.selectedLocationsTarget.querySelector('span').hidden = false
    this.selectedLocationsTarget.querySelector('div').hidden = true
    this.hideSharedActions()
  }

  nextSelectionPage () {
    const maxPage = Math.floor((this.selectionsValue.length - 1) / 10)
    if (this.selectionPageValue < maxPage) {
      this.selectionPageValue++
      this.renderSelectedLocations()
    }
  }

  prevSelectionPage () {
    if (this.selectionPageValue > 0) {
      this.selectionPageValue--
      this.renderSelectedLocations()
    }
  }

  renderSelectedLocations () {
    if (this.selectionsValue.length <= 0) {
      this.hideSelectedLocations()
      return
    }
    const total = this.selectionsValue.length
    const maxPage = Math.floor((total - 1) / 10)
    if (this.selectionPageValue > maxPage) this.selectionPageValue = maxPage

    const start = this.selectionPageValue * 10
    const pageItems = this.selectionsValue.slice(start, start + 10)

    const rows = pageItems.map(sel => {
      const name = sel?.name || ''
      return selectedLocationRow(sel.id, name)
    })

    const remaining = total - (start + pageItems.length)

    let pagination = ''
    if (total > 10) {
      const prevDisabled = this.selectionPageValue === 0
      const nextDisabled = this.selectionPageValue >= maxPage
      pagination = selectedLocationPagination(this.selectionPageValue + 1,
        maxPage + 1, remaining, nextDisabled, prevDisabled)
    }

    this.selectedLocationsListTarget.innerHTML = `
      <div class="p-3">
        ${rows.join('')}
        ${pagination}
      </div>`
    this.showSelectedLocations()
  }

  markSelectedCheckboxes () {
    this.selectedIds.forEach(id => {
      const cb = this.element.querySelector(`input[data-location-id="${id}"]`)
      if (cb) cb.checked = true
    })
    this.renderSelectedLocations()
    this.applySelectedFilter()
    this.updateFilterToggle()
    this.updatePowerControls()
  }

  saveState () {
    const state = {
      selections: this.selectionsValue,
      showSelectedOnly: this.showSelectedOnlyValue,
      selectedLocationId: this.selectedLocationId
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }

  restoreState () {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) {
        this.hideSelectedLocations()
        return
      }
      const state = JSON.parse(raw)
      if (state.selections) {
        this.selectionsValue = state.selections
      } else if (state.selectedLocationIds) {
        this.selectionsValue = state.selectedLocationIds.map(id => ({ id, category: '', name: '', state: '' }))
      }
      this.showSelectedOnlyValue = state.showSelectedOnly || false

      if (state.selectedLocationId) {
        this.selectedLocationId = state.selectedLocationId
        this.showSelectedLocation()
      }
    } catch {
      this.selectionsValue = []
      this.showSelectedOnlyValue = false
    }
    if (this.selectionsValue.length > 0) {
      this.renderSelectedLocations()
      this.updatePowerControls()
    } else {
      this.hideSelectedLocations()
    }
    this.updateFilterToggle()
  }
}
